// src/pages/ScenarioPage/index.tsx
import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, IconButton, Typography, Collapse, CircularProgress } from '@mui/material';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { getScenario } from 'services/scenariosApi';
import type { BackendScenario } from 'services/scenariosApi';
import type { Scenario, ContextFile } from 'types';
import { getScenarioRoleKey } from 'utils/scenarioRole';
import ScenarioSidebar from 'components/ScenarioSidebar';
import ChatPanel from 'components/ChatPanel';
import HistoryPanel from 'components/HistoryPanel';
import ReuploadFilesDialog from 'components/ReuploadFilesDialog';
import { useScenarioSession } from 'scenarios';
import { useAuthStore } from 'store/authStore';

const DEFAULT_MAX_FILES = 5;

function backendToScenario(s: BackendScenario): Scenario {
  return {
    id: s.id,
    number: '01',
    title: s.title,
    shortDescription: s.description ?? '',
    steps: s.action
      ? s.action
          .split('\n')
          .map((l) => l.trim())
          .filter(Boolean)
          .map((label) => ({ label }))
      : [],
    result: s.result ?? '',
    instruction: s.instruction ?? '',
    systemPrompt: s.system_prompt ?? null,
    model: s.model ?? null,
    minFiles: s.min_files ?? 0,
    maxFiles: s.max_files ?? DEFAULT_MAX_FILES,
    roles: [getScenarioRoleKey(s)],
  };
}

export default function ScenarioPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [descOpen, setDescOpen] = useState(false);
  const [scenario, setScenario] = useState<Scenario | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Зашитые в сценарий («статические») документы — всегда участвуют в контексте.
  // Храним целиком (имя/mime/размер), чтобы показать их в сайдбаре для просмотра и скачивания.
  const [scenarioFiles, setScenarioFiles] = useState<ContextFile[]>([]);
  const [deepAnalysis, setDeepAnalysis] = useState(false);

  // Стабильный список id для хука сессии (use_context_files): меняется только при загрузке сценария
  const scenarioDocIds = useMemo(() => scenarioFiles.map((f) => f.id), [scenarioFiles]);

  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    getScenario(id)
      .then((data) => {
        if (data) {
          setScenario(backendToScenario(data));
          setScenarioFiles(data.context_documents ?? []);
        }
      })
      .finally(() => setIsLoading(false));
  }, [id]);

  const session = useScenarioSession(
    id ?? '',
    scenario?.title ?? '',
    user?.role ?? 'lawyer',
    scenario?.maxFiles ?? DEFAULT_MAX_FILES,
    scenario?.systemPrompt ?? null,
    scenarioDocIds
  );

  // Ждём и сам сценарий, и первичное восстановление сессии (история чата) —
  // иначе layout успевает мигнуть idle-видом до подгрузки активного чата
  if (isLoading || session.isRestoring) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (!scenario) {
    return (
      <Box sx={{ p: 4, color: 'var(--color-text-muted)' }}>
        Сценарий не найден.{' '}
        <Box
          component="span"
          onClick={() => navigate('/scenarios')}
          sx={{ color: 'var(--color-primary)', cursor: 'pointer' }}
        >
          Вернуться
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* ── Breadcrumb bar ── */}
      <Box
        sx={{
          px: 2.5,
          py: 0.875,
          bgcolor: 'var(--color-surface)',
          borderBottom: descOpen ? 'none' : '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          flexShrink: 0,
        }}
      >
        <IconButton
          size="small"
          onClick={() => navigate('/scenarios')}
          sx={{
            color: 'var(--color-text-muted)',
            width: 26,
            height: 26,
            '&:hover': { bgcolor: 'var(--color-bg)', color: 'var(--color-text-secondary)' },
          }}
        >
          <ArrowLeft size={15} />
        </IconButton>
        <Box
          sx={{
            fontSize: 12,
            fontWeight: 500,
            color: 'var(--color-text-muted)',
            cursor: 'pointer',
            letterSpacing: '-0.01em',
            '&:hover': { color: 'var(--color-text-secondary)' },
            transition: 'color 0.15s',
          }}
          onClick={() => navigate('/scenarios')}
        >
          Все сценарии
        </Box>

        {/* Divider */}
        <Box sx={{ width: '1px', height: 12, bgcolor: 'var(--color-border)', mx: 0.25 }} />

        {/* Scenario name */}
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--color-text-secondary)',
            letterSpacing: '-0.01em',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {scenario.title}
        </Typography>

        {/* Description toggle */}
        <Box
          onClick={() => setDescOpen((v) => !v)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1.5,
            py: 0.5,
            fontSize: 11.5,
            fontWeight: 700,
            color: '#fff',
            background: 'linear-gradient(135deg, #0A45C0 0%, #1474FF 100%)',
            border: '1px solid transparent',
            borderRadius: 'var(--radius-sm)',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(20,116,255,0.3)',
            transition: 'all 0.15s',
            flexShrink: 0,
            '&:hover': {
              boxShadow: '0 3px 12px rgba(20,116,255,0.45)',
              transform: 'translateY(-1px)',
            },
          }}
        >
          О сценарии
          {descOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
        </Box>
      </Box>

      {/* ── Collapsible description panel ── */}
      <Collapse in={descOpen} unmountOnExit>
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: 'var(--color-surface)',
            borderBottom: '1px solid var(--color-border)',
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 2fr' },
            gap: 3,
            flexShrink: 0,
          }}
        >
          {/* Steps */}
          <Box>
            <Typography
              sx={{
                fontSize: 9.5,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: 'var(--color-text-muted)',
                mb: 1.25,
              }}
            >
              Что делает
            </Typography>
            <Box
              component="ul"
              sx={{
                p: 0,
                m: 0,
                listStyle: 'none',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.625,
              }}
            >
              {scenario.steps.map((step, i) => (
                <Box
                  key={i}
                  component="li"
                  sx={{ display: 'flex', gap: 0.875, alignItems: 'baseline' }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 10,
                      fontWeight: 700,
                      color: 'var(--color-primary)',
                      opacity: 0.7,
                      flexShrink: 0,
                      minWidth: 18,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </Typography>
                  <Typography
                    sx={{ fontSize: 12.5, color: 'var(--color-text-secondary)', lineHeight: 1.5 }}
                  >
                    {step.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Result */}
          <Box>
            <Typography
              sx={{
                fontSize: 9.5,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: 'var(--color-text-muted)',
                mb: 1.25,
              }}
            >
              Результат
            </Typography>
            <Box
              sx={{
                bgcolor: 'rgba(20,116,255,0.05)',
                border: '1px solid rgba(20,116,255,0.15)',
                borderRadius: 'var(--radius-md)',
                px: 1.5,
                py: 1.125,
                fontSize: 12.5,
                color: 'var(--color-text-secondary)',
                lineHeight: 1.55,
              }}
            >
              {scenario.result}
            </Box>
          </Box>

          {/* Instruction */}
          <Box>
            <Typography
              sx={{
                fontSize: 9.5,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: 'var(--color-text-muted)',
                mb: 1.25,
              }}
            >
              Инструкция
            </Typography>
            <Box
              sx={{
                bgcolor: 'var(--color-bg)',
                borderLeft: '2.5px solid var(--color-primary)',
                borderRadius: '0 var(--radius-sm) var(--radius-sm) 0',
                px: 1.75,
                py: 1.125,
                fontSize: 12.5,
                color: 'var(--color-text-secondary)',
                lineHeight: 1.65,
                maxHeight: 100,
                overflowY: 'auto',
              }}
            >
              {scenario.instruction}
            </Box>
          </Box>
        </Box>
      </Collapse>

      {/* ── Main 3-column area ── */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <ScenarioSidebar
          scenario={scenario}
          sessionStatus={session.status}
          // При просмотре чата из истории список «в работе» = активные загруженные
          // документы этого чата (из view, без зашитых). Зашитые файлы сценария
          // показываем отдельным блоком всегда — и на чистом сценарии, и в истории.
          files={session.historyFiles ?? session.files}
          scenarioFiles={scenarioFiles}
          selectedFileIds={session.selectedFileIds}
          isFilesLoading={session.isFilesLoading}
          isUploading={session.isUploading}
          uploadError={session.uploadError}
          deletingFileId={session.deletingFileId}
          onSelectFile={session.selectFile}
          onDeselectFile={session.deselectFile}
          onUploadFiles={session.uploadFiles}
          onDeleteFile={session.deleteFile}
          onRunScenario={() => session.runScenario(deepAnalysis)}
          onResetSession={session.resetSession}
          onCancelRun={session.cancelRun}
          deepAnalysis={deepAnalysis}
          onDeepAnalysisChange={setDeepAnalysis}
        />
        <ChatPanel
          sessionStatus={session.status}
          messages={session.messages}
          streamingMessage={session.streamingMessage}
          displayedContent={session.displayedContent}
          progressLabel={session.progressLabel}
          isLoading={session.isLoading}
          onSendMessage={(text) => session.sendMessage(text, deepAnalysis)}
          onStopGeneration={session.stopGeneration}
          scenarioId={id ?? ''}
          modelName={scenario.model?.name ?? null}
          obfuscating={session.obfuscating}
          modelExternal={session.modelExternal}
        />
        <HistoryPanel
          chatHistory={session.chatHistory}
          activeChatId={session.chatId}
          onSelectChat={session.loadChat}
          onDeleteChat={session.deleteChat}
          deletingChatId={session.deletingChatId}
        />
      </Box>

      {/* ── 422 «загрузите повторно»: документы старого формата ── */}
      <ReuploadFilesDialog
        open={!!session.reuploadFileNames}
        fileNames={session.reuploadFileNames ?? []}
        files={session.files}
        projectId={user?.projectId ?? ''}
        targetChatId={session.filesChatId}
        onClose={session.closeReuploadDialog}
        onReuploaded={session.handleReuploaded}
      />
    </Box>
  );
}
